import React, { useState } from 'react';
import { CommonPageStep } from '..';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  ButtonBase,
  ButtonBaseProps,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from '@mui/material';
import { style } from '@mui/system';
import * as Sentry from '@sentry/react';
import fetch from 'cross-fetch';
import _, { debounce } from 'lodash';
import { isMobileOnly, useMobileOrientation } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import styles from './LoginElement.module.css';
import { ReactComponent as ParentIcon } from './parentIcon.svg';
import { ReactComponent as StudentIcon } from './studentIcon.svg';
import apiHelper, {
  SupportedIncomingFormat,
  SupportedMethod,
  SupportedOutgoingFormat,
  SupportedServers,
} from '../../common/ApiHelper';
import { appConfig } from '../../common/ConfigLoader';
import { redirectToResetPw } from '../../common/PageRedirection';
import { useAppSelector } from '../../store/hooks';
import {
  setAppPageStep,
  updateAppAuthentication,
} from '../../store/reducer/appStateSlice';
import { outsideComponentDispatch } from '../../store/store';
import { redirectToMaintenancePage } from '../systemMaintenance/systemMaintenance';

export interface LoginElementInterface {
  loginButton: JSX.Element;
  emailInput: JSX.Element;
  passwordInput: JSX.Element;
  loginModeSelection: (height?: number) => JSX.Element;
  forgetEmailButtonClickHandler: () => void;
  forgetPasswordButtonClickHandler: () => void;
  rememberMeCheckbox: JSX.Element;
  backButtonClickHandler: () => void;
  signUpRedirectButtonClickHandler: () => void;
  loginMode: 'bbl' | 'parentPortal';
}

export const LoginElement = (): LoginElementInterface => {
  // Redux

  // Hooks
  const [emailInputError, setEmailInputError] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [passwordInputError, setPasswordInputError] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(true);

  const emailRef = React.useRef<HTMLInputElement>();
  const passwordRef = React.useRef<HTMLInputElement>();
  const { isLandscape } = useMobileOrientation();

  // Handlers
  const loginBackButtonClickHandler = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    console.log('Redirecting to /Entry');
    outsideComponentDispatch(setAppPageStep(CommonPageStep.ENTRY));
  };

  const loginLoginButtonClickHandler = async (
    event?: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    const username = emailRef.current!.value.trim().toLowerCase();
    const password = passwordRef.current!.value.trim();
    const emailExist = await checkEmailExist(username);

    if (emailExist) {
      // LOGIN TO SUBSCRIPTION
      const loginEndPoint = apiHelper(
        SupportedServers.ACCOUNT,
        `user/UserLogin`,
        SupportedMethod.POST,
        {
          username,
          password,
        },
        {
          incomingFormat: SupportedIncomingFormat.JSON,
          outgoingFormat: SupportedOutgoingFormat.JSON,
        },
      ).then((res) => {
        if (res.success) {
          if (res.data?.token) {
            // Login Successful
            if (res.data.needResetPassword === true) {
              redirectToResetPw(res.data.username);
            } else {
              // STORE AUTHENTICATION DATA TO LOCAL STORAGAGE
              const sessionInfoString = JSON.stringify({ ...res.data });
              if (rememberMe) {
                window.localStorage.setItem(
                  'bigbang_session_info',
                  sessionInfoString,
                );
                window.localStorage.setItem(
                  'bigbang_session_target',
                  isLandscape ? 'BBL' : 'PP',
                );
              }

              // UPDATE REDUX AUTHENTICATION DATA
              outsideComponentDispatch(updateAppAuthentication(res.data));
              // @ts-expect-error Appcues run-time-script
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              window.Appcues?.identify(res.data.userId);
              console.log(res.data);
              console.log({
                username: res.data.userId,
                ip_address: '{{auto}}',
              });

              Sentry.setUser({
                id: res.data.userId,
                username: res.data.username,
                email: res.data.username,
                ip_address: '{{auto}}',
              });
            }
          } else {
            // HOTFIX: DISABLE EMAIL ERROR MSG IF PASSWORD ERROR
            // setEmailInputError(true);
            setPasswordInputError(true);
          }
        }
      });
    }
  };

  const onEmailInputChange = debounce(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      void checkEmailExist(event.target.value.trim().toLowerCase());
    },
    500,
  );

  const checkEmailExist = async (email: string): Promise<boolean> => {
    setEmailInputError(false);
    console.debug('SET EMAIL', email);
    // CHECK IF USERNAME EXISTED OR NOT
    const usernameVerifyEndpoint = 'user/UsernameVerify';
    const usernameVerifyData = {
      username: email,
    };
    const usernameVerifyResult = await apiHelper(
      SupportedServers.ACCOUNT,
      usernameVerifyEndpoint,
      SupportedMethod.POST,
      usernameVerifyData,
      {
        incomingFormat: SupportedIncomingFormat.JSON,
        outgoingFormat: SupportedOutgoingFormat.JSON,
      },
    );

    if (usernameVerifyResult.success) {
      if (!usernameVerifyResult.data) {
        // CANNOT FOUND USER
        setEmailInputError(true);
        return false;
      }
    }

    return true;
  };

  const onPasswordInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setPasswordInputError(false);
  };

  const [loginMode, setLoginMode] = useState<'bbl' | 'parentPortal'>(
    isMobileOnly ? 'parentPortal' : 'bbl',
  );

  const loginModeSelection = (height?: number): JSX.Element => {
    return (
      <Box className={styles.loginModeSelection}>
        <span className={styles.description}>Please select your role</span>
        <Box className={styles.loginModeSelectionBoxes}>
          <Box
            className={
              loginMode === 'bbl'
                ? `${styles.selectionBox} ${styles.selected}`
                : styles.selectionBox
            }
            style={height ? { height, width: height } : undefined}
            onClick={(): void => {
              setLoginMode('bbl');
            }}
          >
            <StudentIcon
              fill={loginMode === 'bbl' ? '#FF8429' : '#FFDCC2'}
              className={styles.icon}
            />
            <span className={styles.desc}>Student</span>
            {loginMode === 'bbl' && (
              <DoneRoundedIcon className={styles.doneIcon} />
            )}
          </Box>
          <Box
            className={
              loginMode === 'parentPortal'
                ? `${styles.selectionBox} ${styles.selected}`
                : styles.selectionBox
            }
            style={height ? { height, width: height } : undefined}
            onClick={(): void => {
              setLoginMode('parentPortal');
            }}
          >
            <ParentIcon
              fill={loginMode === 'parentPortal' ? '#FF8429' : '#FFDCC2'}
              className={styles.icon}
            />
            <span className={styles.desc}>Parent</span>
            {loginMode === 'parentPortal' && (
              <DoneRoundedIcon className={styles.doneIcon} />
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return {
    loginModeSelection,
    loginMode,
    loginButton: (
      <Button
        variant="contained"
        sx={{ width: '100%' }}
        fullWidth={true}
        onClick={loginLoginButtonClickHandler}
      >
        Log In
      </Button>
    ),
    emailInput: (
      <FormControl
        sx={{ m: 0, width: '100%' }}
        variant="outlined"
        error={emailInputError}
      >
        <InputLabel htmlFor="outlined-adornment-email">Email</InputLabel>
        <OutlinedInput
          type="text"
          size="medium"
          margin="none"
          label="Email"
          error={emailInputError}
          onChange={onEmailInputChange}
          inputRef={emailRef}
          fullWidth={true}
          sx={{ width: 'stretch' }}
          onKeyDown={({ key }): void => {
            if (key === 'Enter') {
              void loginLoginButtonClickHandler();
            }
          }}
        />
        <FormHelperText id="email-input-helper-text">
          {emailInputError ? '❗The email you entered is incorrect.' : ''}
        </FormHelperText>
      </FormControl>
    ),
    passwordInput: (
      <FormControl
        sx={{ m: 0, width: '100%' }}
        variant="outlined"
        error={passwordInputError}
      >
        <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
        <OutlinedInput
          id="outlined-adornment-password"
          type={showPassword ? 'text' : 'password'}
          sx={{ width: 'stretch' }}
          inputRef={passwordRef}
          fullWidth={true}
          onInputCapture={onPasswordInputChange}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={(): void => {
                  setShowPassword(!showPassword);
                }}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Password"
          aria-describedby="password-input-helper-text"
          onKeyDown={({ key }): void => {
            if (key === 'Enter') {
              void loginLoginButtonClickHandler();
            }
          }}
        />
        <FormHelperText id="password-input-helper-text">
          {passwordInputError
            ? '❗️The password you entered is incorrect.'
            : ''}
        </FormHelperText>
      </FormControl>
    ),
    forgetEmailButtonClickHandler() {
      outsideComponentDispatch(setAppPageStep(CommonPageStep.FORGETEMAIl));
    },
    forgetPasswordButtonClickHandler() {
      outsideComponentDispatch(setAppPageStep(CommonPageStep.FORGETPASSWORD));
    },
    rememberMeCheckbox: (
      <FormControlLabel
        label="Remember me"
        labelPlacement="end"
        color="primary"
        control={
          <Checkbox
            color="primary"
            defaultChecked
            size="medium"
            checked={rememberMe}
            onChange={(): void => {
              setRememberMe(!rememberMe);
            }}
          />
        }
      />
    ),
    backButtonClickHandler(): void {
      console.log('Redirecting to /Entry');
      outsideComponentDispatch(setAppPageStep(CommonPageStep.ENTRY));
      // To-do: Add transition animation
      // window.location.href = '/Entry';
    },
    signUpRedirectButtonClickHandler(): void {
      outsideComponentDispatch(setAppPageStep(CommonPageStep.SIGNUP));
      // To-do: Add transition animation
      // window.location.href = '/Signup';
    },
  };
};
